import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import NotificationSnackbar from "../../../../common/notificationBar/notification-snackbar";

const TasksExportConcerns = ({ filteredTasks }) => {
  const [openExportSnackbar, setExportOpenSnackbar] = useState(false);

  const renderTable = (doc, title, data, columns, startY = null) => {
    if (startY) doc.setPage(doc.internal.getNumberOfPages());

    doc.setFontSize(14);
    doc.text(title, 14, startY || doc.lastAutoTable?.finalY + 10 || 30);

    autoTable(doc, {
      head: [columns],
      body: data,
      startY: startY || doc.lastAutoTable?.finalY + 15,
    });
  };

  const formatDateSafe = (dateValue) => {
    try {
      const parsed = new Date(dateValue);
      if (!parsed || isNaN(parsed)) return "N/A";
      return format(parsed, "dd MMM yyyy HH:mm");
    } catch {
      return "N/A";
    }
  };

  const flattenJSON = (jsonObj) =>
    Object.entries(jsonObj || {}).map(([key, value]) => {
      if (typeof value === "object" && value !== null) {
        try {
          return [key, JSON.stringify(value, null, 2)];
        } catch {
          return [key, "[Invalid JSON]"];
        }
      } else {
        return [key, String(value)];
      }
    });

  const handleExportToPDF = () => {
    if (!filteredTasks) return;

    const doc = new jsPDF();
    const formattedDate = formatDateSafe(new Date());

    doc.setFontSize(18);
    doc.text("Cause for Concern Report", 14, 20);

    const {
      title,
      place,
      incidentTime,
      dateInformed,
      gender,
      updated_at,
      incidentInfo,
      witnessData,
      imageInfo,
      witnessInfo,
      uploadedFiles,
      uploadedImages,
      roomSearch, // assume roomSearch is passed in as part of filteredTasks
    } = filteredTasks;

    const causeConcernGeneral = [
      ["Title", title],
      ["Place", place],
      ["Incident Time", incidentTime],
      ["Date Informed", formatDateSafe(dateInformed)],
      ["Gender", gender || "N/A"],
      ["Updated At", formatDateSafe(updated_at)],
    ];

    autoTable(doc, {
      startY: 30,
      head: [],
      body: causeConcernGeneral,
      theme: "plain",
      styles: { fontSize: 12 },
    });

    renderTable(doc, "Incident Info", flattenJSON(incidentInfo), ["Field", "Value"]);
    renderTable(doc, "Witness Data", flattenJSON(witnessData), ["Field", "Value"]);
    renderTable(doc, "Witness Info", flattenJSON(witnessInfo), ["Field", "Value"]);
    renderTable(doc, "Image Info", flattenJSON(imageInfo), ["Field", "Value"]);
    renderTable(doc, "Uploaded Files", flattenJSON(uploadedFiles), ["Field", "Value"]);
    renderTable(doc, "Uploaded Images", flattenJSON(uploadedImages), ["Field", "Value"]);

    // Room Search Section
    if (roomSearch) {
      renderTable(
        doc,
        "Room Search - General Info",
        [
          ["Task Name", roomSearch.taskName],
          ["Date of Search", formatDateSafe(roomSearch.dateOfSearch)],
          ["Time of Search", roomSearch.timeOfSearch || "N/A"],
          ["Updated At", formatDateSafe(roomSearch.updated_at)],
        ],
        ["Field", "Value"]
      );

      renderTable(doc, "Room Search - Witness Data", flattenJSON(roomSearch.witnessData), ["Field", "Value"]);
      renderTable(doc, "Room Search - Audio Records", flattenJSON(roomSearch.audioRecords), ["Field", "Value"]);
    }

    doc.save(`cause-for-concern-${formattedDate}.pdf`);
    setExportOpenSnackbar(true);
  };

  return (
    <Box>
      <Typography variant='subtitle1' sx={{ fontSize: "18px", fontWeight: "bold" }} align='left'>
        Export Concern Report
      </Typography>
      <Typography variant='body2' sx={{ fontSize: "14px", mb: 3 }} align='left'>
        Click below to export the full report with attached room search records.
      </Typography>
      <Button
        variant='contained'
        color='primary'
        onClick={handleExportToPDF}
        sx={{ width: "100%", textTransform: "none" }}
      >
        Export
      </Button>
      <NotificationSnackbar
        open={openExportSnackbar}
        onClose={() => setExportOpenSnackbar(false)}
        color='success'
        message='Report exported successfully.'
      />
    </Box>
  );
};

export default TasksExportConcerns;
